import { useCallback, useContext, useMemo } from 'react';
import {
  NINJA_AGENT_TEXT_AUTO_MODEL,
  FIRST_COMPARING_CONV_PARAM,
  SECOND_COMPARE_MODEL_PARAM,
  SUPER_AGENT_REASONING_MODEL,
} from 'src/constants';
import ThreadInputBoxContext, {
  ThreadInputBoxContextType,
} from 'src/contexts/ThreadInputBoxContext';
import {
  useCompareMode,
  useComparingConversations,
  useConversationActions,
  useSkillCommand,
  useSubmitUserInput,
} from 'src/hooks';

export const useCompareModeSubmit = () => {
  const { firstCompareModel, secondCompareModel } = useCompareMode();
  const { updateConversationById } = useConversationActions();
  const { onSubmitUserInput } = useSubmitUserInput();
  const { isThinkEnabled } = useSkillCommand();
  const {
    isComparingConversationsLoaded,
    firstConversationData,
    secondConversationData,
  } = useComparingConversations();

  const { threadInputBoxValue } = useContext<ThreadInputBoxContextType>(
    ThreadInputBoxContext,
  );

  const onCompareModeSubmit = useCallback(
    async (userInput?: string) => {
      const [firstConversationResult, secondConversationResult] =
        await Promise.all([
          onSubmitUserInput({
            userInput: userInput || threadInputBoxValue,
            customParams: {
              overrideModelName: isThinkEnabled
                ? SUPER_AGENT_REASONING_MODEL
                : firstCompareModel || NINJA_AGENT_TEXT_AUTO_MODEL,
              compareSearchParamName: FIRST_COMPARING_CONV_PARAM,
              comparedConvId: isComparingConversationsLoaded
                ? firstConversationData?.conversation_id
                : undefined,
            },
          }),
          onSubmitUserInput({
            userInput: userInput || threadInputBoxValue,
            customParams: {
              overrideModelName:
                secondCompareModel || NINJA_AGENT_TEXT_AUTO_MODEL,
              compareSearchParamName: SECOND_COMPARE_MODEL_PARAM,
              comparedConvId: isComparingConversationsLoaded
                ? secondConversationData?.conversation_id
                : undefined,
            },
          }),
        ]);

      if (firstConversationResult && secondConversationResult) {
        const firstConversationPayload = JSON.parse(
          firstConversationResult.payload,
        );
        const secondConversationPayload = JSON.parse(
          secondConversationResult.payload,
        );

        updateConversationById({
          conversation_id: firstConversationPayload.conversation_id,
          linked_conversation_id: secondConversationPayload.conversation_id,
        });

        updateConversationById({
          conversation_id: secondConversationPayload.conversation_id,
          linked_conversation_id: firstConversationPayload.conversation_id,
        });
      }
    },
    [
      onSubmitUserInput,
      threadInputBoxValue,
      isThinkEnabled,
      firstCompareModel,
      isComparingConversationsLoaded,
      firstConversationData?.conversation_id,
      secondCompareModel,
      secondConversationData?.conversation_id,
      updateConversationById,
    ],
  );

  return useMemo(
    () => ({
      onCompareModeSubmit,
    }),
    [onCompareModeSubmit],
  );
};
